// src/components/Footer/Footer.js

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaChevronDown,
} from "react-icons/fa";

const Footer = () => {
  const { translate } = useLanguage();

  const [navOpen, setNavOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const t = (key, fallback) => translate(key) || fallback;

  const currentYear = new Date().getFullYear();

  const navigationLinks = [
    { to: "/", label: t("home", "Inicio") },
    { to: "/company", label: t("company", "Compañía") },
    { to: "/operations", label: t("operations", "Operaciones") },
    { to: "/sustainability", label: t("sustainability", "Sostenibilidad") },
    { to: "/services", label: t("services", "Servicios") },
    { to: "/contact-us", label: t("Contáctanos", "Contáctanos") },
  ];

  const resourceLinks = [
    { to: "/work-with-us", label: t("workWithUs", "Trabaja con Nosotros") },
    { to: "/pqrs", label: t("pqrs", "PQRS") },
    { to: "/code-of-ethics", label: t("codeOfEthics", "Código de Ética") },
    { to: "/certifications", label: t("certifications", "Certificaciones") },
    { to: "/brochure", label: "Brochure" },
  ];

  return (
    <footer className="bg-[#f5f5f5] border-t border-gray-200">
      <div className="max-w-[1400px] mx-auto px-[6%] py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="text-center lg:text-left flex flex-col items-center lg:items-start">
            <Link to="/">
              <img
                src="/Images/gsa_logo003.webp"
                alt="Sierra Alta"
                loading="lazy"
                className="w-[170px] mb-5 opacity-90"
              />
            </Link>

            <p className="text-[14px] leading-[1.8] text-gray-600 max-w-[280px]">
              Energy & Environmental Solutions
            </p>
          </div>

          <div>
            <button
              onClick={() => setNavOpen(!navOpen)}
              className="
                md:hidden
                w-full
                flex
                items-center
                justify-between
                py-3
                border-b
                border-gray-200
              "
            >
              <span className="font-semibold text-[#111]">Navegación</span>

              <FaChevronDown
                className={`transition-transform duration-300 ${
                  navOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <h3 className="hidden md:block text-[17px] font-semibold text-[#111] mb-6">
              Navegación
            </h3>

            <ul
              className={`
                space-y-4
                overflow-hidden
                transition-all
                duration-300
                md:max-h-full
                ${navOpen ? "max-h-[400px] pt-5" : "max-h-0 md:max-h-full"}
              `}
            >
              {navigationLinks.map((link, index) => (
                <li key={index} className="text-center md:text-left">
                  <Link
                    to={link.to}
                    className="
                      text-[15px]
                      text-gray-600
                      hover:text-[#f37021]
                      transition-colors
                      duration-300
                    "
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <button
              onClick={() => setResourcesOpen(!resourcesOpen)}
              className="
                md:hidden
                w-full
                flex
                items-center
                justify-between
                py-3
                border-b
                border-gray-200
              "
            >
              <span className="font-semibold text-[#111]">Recursos</span>

              <FaChevronDown
                className={`transition-transform duration-300 ${
                  resourcesOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <h3 className="hidden md:block text-[17px] font-semibold text-[#111] mb-6">
              Recursos
            </h3>

            <ul
              className={`
                space-y-4
                overflow-hidden
                transition-all
                duration-300
                md:max-h-full
                ${
                  resourcesOpen ? "max-h-[400px] pt-5" : "max-h-0 md:max-h-full"
                }
              `}
            >
              {resourceLinks.map((link, index) => (
                <li key={index} className="text-center md:text-left">
                  <Link
                    to={link.to}
                    className="
                      text-[15px]
                      text-gray-600
                      hover:text-[#f37021]
                      transition-colors
                      duration-300
                    "
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <button
              onClick={() => setContactOpen(!contactOpen)}
              className="
                md:hidden
                w-full
                flex
                items-center
                justify-between
                py-3
                border-b
                border-gray-200
              "
            >
              <span className="font-semibold text-[#111]">Contacto</span>

              <FaChevronDown
                className={`transition-transform duration-300 ${
                  contactOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <h3 className="hidden md:block text-[17px] font-semibold text-[#111] mb-6">
              Contacto
            </h3>

            <div
              className={`
                space-y-5
                overflow-hidden
                transition-all
                duration-300
                md:max-h-full
                ${contactOpen ? "max-h-[700px] pt-5" : "max-h-0 md:max-h-full"}
              `}
            >
              <div className="flex gap-4 items-start justify-center md:justify-start text-center md:text-left">
                <div
                  className="
                  min-w-[40px]
                  h-[40px]
                  border border-gray-200
                  flex items-center justify-center
                  text-[#f37021]
                "
                >
                  <FaMapMarkerAlt />
                </div>

                <div>
                  <h4 className="text-[14px] font-semibold text-[#111] mb-1">
                    Oficina Administrativa
                  </h4>

                  <p className="text-[14px] text-gray-600 leading-[1.7]">
                    Calle 113 #7 - 45 Oficina 816
                  </p>

                  <span className="text-[13px] text-gray-500">Bogotá DC</span>
                </div>
              </div>

              <div className="flex gap-4 items-start justify-center md:justify-start text-center md:text-left">
                <div
                  className="
                  min-w-[40px]
                  h-[40px]
                  border border-gray-200
                  flex items-center justify-center
                  text-[#f37021]
                "
                >
                  <FaMapMarkerAlt />
                </div>

                <div>
                  <h4 className="text-[14px] font-semibold text-[#111] mb-1">
                    Laboratorio
                  </h4>

                  <p className="text-[14px] text-gray-600 leading-[1.7]">
                    Calle 141 #47 - 43
                  </p>

                  <span className="text-[13px] text-gray-500">Bogotá DC</span>
                </div>
              </div>

              <a
                href="mailto:info@sierraalta.com.co"
                className="
                  flex
                  items-center
                  gap-4
                  justify-center
                  md:justify-start
                  text-gray-600
                  hover:text-[#f37021]
                  transition-colors
                  duration-300
                "
              >
                <div
                  className="
                  min-w-[40px]
                  h-[40px]
                  border border-gray-200
                  flex items-center justify-center
                  text-[#f37021]
                "
                >
                  <FaEnvelope />
                </div>

                <span className="text-[14px]">info@sierraalta.com.co</span>
              </a>

              <a
                href="tel:+573116823235"
                className="
                  flex
                  items-center
                  gap-4
                  justify-center
                  md:justify-start
                  text-gray-600
                  hover:text-[#f37021]
                  transition-colors
                  duration-300
                "
              >
                <div
                  className="
                  min-w-[40px]
                  h-[40px]
                  border border-gray-200
                  flex items-center justify-center
                  text-[#f37021]
                "
                >
                  <FaPhoneAlt />
                </div>

                <span className="text-[14px]">+57 311 682 3235</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#f5f5f5] border-t border-gray-200">
        <div
          className="
      max-w-[1400px]
      mx-auto
      px-[6%]
      py-5
      flex
      flex-col
      lg:flex-row
      items-center
      justify-between
      gap-6
    "
        >
          <div
            className="
        flex
        flex-col
        lg:flex-row
        items-center
        gap-4
        text-center
        lg:text-left
      "
          >
            <p className="text-[13px] text-gray-500 leading-none">
              © 2021 - {currentYear} Sierra Alta SAS
            </p>

            <div className="hidden lg:flex items-center gap-3">
              <span className="w-[4px] h-[4px] rounded-full bg-gray-300" />

              <Link
                to="/policies"
                className="
            text-[13px]
            text-gray-500
            hover:text-[#f37021]
            transition-colors
            duration-300
          "
              >
                Políticas de Privacidad
              </Link>

              <span className="w-[4px] h-[4px] rounded-full bg-gray-300" />

              <Link
                to="/terms"
                className="
            text-[13px]
            text-gray-500
            hover:text-[#f37021]
            transition-colors
            duration-300
          "
              >
                Términos y Condiciones
              </Link>
            </div>

            <div className="flex lg:hidden items-center gap-4 flex-wrap justify-center">
              <Link
                to="/policies"
                className="
            text-[13px]
            text-gray-500
            hover:text-[#f37021]
            transition-colors
            duration-300
          "
              >
                Privacidad
              </Link>

              <Link
                to="/terms"
                className="
            text-[13px]
            text-gray-500
            hover:text-[#f37021]
            transition-colors
            duration-300
          "
              >
                Términos
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-5">
            <a
              href="https://www.linkedin.com/company/grupo-sierra-alta-sas"
              target="_blank"
              rel="noopener noreferrer"
              className="
      flex items-center justify-center
      hover:opacity-80
      hover:-translate-y-[2px]
      transition-all duration-300
    "
            >
              <img
                src="/Images/icons-01.webp"
                alt="LinkedIn"
                className="
        w-[34px]
        object-contain
      "
              />
            </a>

            <a
              href="https://campetrol.org/directorio/afiliado/grupo-sierra-alta-s-a-s/"
              target="_blank"
              rel="noopener noreferrer"
              className="
      flex items-center justify-center
      hover:opacity-80
      hover:-translate-y-[2px]
      transition-all duration-300
    "
            >
              <img
                src="/Images/icons-02.webp"
                alt="Campetrol"
                className="
        w-[34px]
        object-contain
      "
              />
            </a>

            <a
              href="https://mivitrina.parservicios.com/proveedor/grupo-sierra-alta-sas"
              target="_blank"
              rel="noopener noreferrer"
              className="
      flex items-center justify-center
      hover:opacity-80
      hover:-translate-y-[2px]
      transition-all duration-300
    "
            >
              <img
                src="/Images/icons-03.webp"
                alt="Par Servicios"
                className="
        w-[34px]
        object-contain
      "
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
